import {
    Box,
    Skeleton,
    Stack,
    Text,
    ThemeColorVariable,
    ThemeFontSizeVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import { RichTextRenderer } from "components/rich-text-renderer";
import { SafeHTML } from "components/safe-html";
import ShowMoreText from "components/show-more-text/show-more-text";
import { useSelectedRule } from "hooks/use-selected-rule";
import { resolveRichTextField } from "types/rich-text-field";
import { WebRule } from "types/web-rule";
import { jsonToRichTextField } from "utils/json-to-rich-text-field";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useExternalBookingSettings } from "./../../api/api-hooks/use-external-booking-settings";
import { useIsMutating } from "@tanstack/react-query";

/**
 * The finalize page shows a single "Information/Terms" block fed by three independent sources.
 * They are assembled here and nowhere else — add new sources to this file rather than rendering
 * terms from another component.
 *
 * - Venue terms: set per unit under admin Settings. Two generations exist; the rich-text
 *   `messageTemplates.webConditions` supersedes the plain-text `webConditions` whenever it is
 *   present. Exactly one of them renders, never both.
 * - Rule terms: a plain webrule's own `conditions`, set per rule under admin Webrules.
 * - Experience terms: `experienceBookingConditions`, authored once on the experience. The API
 *   generates one webrule per activity x schedule x day and stamps the same terms onto every one
 *   of them, and a booking is attached to one rule per booked activity. They must therefore be
 *   collapsed per experience, or a multi-activity booking repeats the same text once per activity.
 */
function BookingConditions() {
    const { t, i18n } = useTranslation();
    const { data: booking, isLoading: isBookingLoading } = useWebBooking();
    const { data: settings, isLoading: isSettingsLoading } = useExternalBookingSettings();
    const rule = useSelectedRule();
    const isCreatingBooking = useIsMutating({ mutationKey: ["create-booking"] }) > 0;

    // Show skeleton while essential data is loading
    if (isCreatingBooking || isBookingLoading || isSettingsLoading) {
        return (
            <Stack spacing={ThemeSpaceVariable.Small}>
                <Skeleton height="1.5rem" width="50%" />
                <Stack spacing={ThemeSpaceVariable.Medium}>
                    <Skeleton height="1rem" width="100%" />
                    <Skeleton height="1rem" width="90%" />
                </Stack>
            </Stack>
        );
    }

    const venueTermsTemplate = settings?.messageTemplates?.webConditions;
    const legacyVenueTerms = settings?.webConditions?.trim();
    const bookedRuleTerms = renderBookedRuleTerms(booking?.webBookingRules, i18n.language);

    if (!venueTermsTemplate && !legacyVenueTerms && bookedRuleTerms.length === 0) return null;

    const venueTermsNode = venueTermsTemplate ? (
        <RichTextRenderer content={resolveRichTextField(venueTermsTemplate, i18n.language)} />
    ) : legacyVenueTerms ? (
        <SafeHTML html={legacyVenueTerms} />
    ) : null;

    const termsNode = (
        <>
            {venueTermsNode && (
                <Box
                    wordBreak="break-word"
                    overflowWrap="anywhere"
                    mb={bookedRuleTerms.length > 0 ? ThemeSpaceVariable.Medium : undefined}
                >
                    {venueTermsNode}
                </Box>
            )}
            {bookedRuleTerms.length > 0 && (
                // Stack, not Box: each booked rule's terms is a separate source and gets its own
                // row. SafeHTML renders an inline span, so plain rule terms would otherwise run
                // together on one line.
                <Stack
                    spacing={ThemeSpaceVariable.Small}
                    wordBreak="break-word"
                    overflowWrap="anywhere"
                >
                    {bookedRuleTerms}
                </Stack>
            )}
        </>
    );

    return (
        <Stack spacing={ThemeSpaceVariable.Small}>
            <Text fontSize={ThemeFontSizeVariable.Large} fontWeight={"medium"}>
                {t("informationAndConditions")}
            </Text>
            <Stack spacing={ThemeSpaceVariable.Medium}>
                {settings?.collapseConditions ? (
                    <ShowMoreText initialLines={3} backgroundColor={ThemeColorVariable.Background}>
                        {termsNode}
                    </ShowMoreText>
                ) : (
                    termsNode
                )}
                {rule?.hasNoShowRule() && !!booking?.chargeShouldPayAmount && (
                    <Text>
                        {t("noShowCondition", {
                            value: booking.chargeShouldPayAmount,
                            amount: "symbol",
                        })}
                    </Text>
                )}
            </Stack>
        </Stack>
    );
}

/**
 * One terms node per distinct source: rules generated from the same experience collapse to a
 * single node, plain webrules stay keyed by rule so each rule's own terms still show.
 */
function renderBookedRuleTerms(rules: WebRule[] | null | undefined, language: string): ReactNode[] {
    const renderedSources = new Set<string>();
    const nodes: ReactNode[] = [];

    for (const rule of rules ?? []) {
        const sourceKey = rule.experienceId ? `experience:${rule.experienceId}` : `rule:${rule.id}`;
        if (renderedSources.has(sourceKey)) continue;

        const experienceTerms = jsonToRichTextField(rule.experienceBookingConditions);
        if (experienceTerms) {
            renderedSources.add(sourceKey);
            nodes.push(
                <RichTextRenderer
                    key={sourceKey}
                    content={resolveRichTextField(experienceTerms, language)}
                />
            );
            continue;
        }

        if (rule.conditions?.trim()) {
            renderedSources.add(sourceKey);
            nodes.push(<SafeHTML key={sourceKey} html={rule.conditions} />);
        }
    }

    return nodes;
}

export default BookingConditions;
